import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { quizApi } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { getArchetypeContent } from "@/data/archetypeContent";
import Loading from "@/components/app/Loading";
import { IDS } from "@/constants/testIds";
import { Lock } from "lucide-react";

function Section({ title, children }) {
  return (
    <section className="pa-card p-5 md:p-6">
      <h3 className="pa-serif text-lg md:text-xl mb-3" style={{ color: "var(--pa-gold)" }}>{title}</h3>
      <div className="space-y-3 text-sm md:text-base leading-relaxed" style={{ color: "var(--pa-text-primary)" }}>
        {children}
      </div>
    </section>
  );
}

function ArchetypeInsights({ tag, name }) {
  const c = getArchetypeContent(name);
  if (!c) return null;
  return (
    <div className="pa-card p-5 md:p-6">
      <div className="flex items-center gap-4 mb-4">
        <img src={c.emblem} alt={c.name} className="w-16 h-16 object-contain" />
        <div>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--pa-gold)" }}>{tag}</div>
          <h3 className="pa-serif text-xl md:text-2xl" style={{ color: "var(--pa-text-primary)" }}>{c.name}</h3>
          {c.coreOrientation && (
            <p className="text-xs md:text-sm" style={{ color: "var(--pa-text-secondary)" }}>{c.coreOrientation}</p>
          )}
        </div>
      </div>
      <div className="space-y-4">
        {c.coreMessage && (
          <blockquote
            className="pa-serif italic text-base md:text-lg pl-4 border-l-2"
            style={{ borderColor: "var(--pa-gold)", color: "var(--pa-text-secondary)" }}
          >
            {c.coreMessage}
          </blockquote>
        )}
        <div>
          <h4 className="text-sm font-semibold mb-2" style={{ color: "var(--pa-gold-light)" }}>Role</h4>
          <p className="text-sm whitespace-pre-line" style={{ color: "var(--pa-text-primary)" }}>{c.role}</p>
        </div>
        <div>
          <h4 className="text-sm font-semibold mb-2" style={{ color: "var(--pa-gold-light)" }}>Primary Strengths</h4>
          <ul className="text-sm space-y-1 list-disc list-inside" style={{ color: "var(--pa-text-primary)" }}>
            {c.primaryStrengths.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-semibold mb-2" style={{ color: "var(--pa-gold-light)" }}>Shadow Tendency</h4>
          <p className="text-sm" style={{ color: "var(--pa-text-primary)" }}>{c.shadowTendency}</p>
        </div>
        <div>
          <h4 className="text-sm font-semibold mb-2" style={{ color: "var(--pa-gold-light)" }}>Interfaces With The Unknown</h4>
          <p className="text-sm whitespace-pre-line" style={{ color: "var(--pa-text-primary)" }}>{c.interfacesWithUnknown}</p>
        </div>
      </div>
      <div className="mt-4">
        <Link to={`/archetype/${encodeURIComponent(name)}`} className="pa-btn-outline inline-block text-sm">
          Full profile
        </Link>
      </div>
    </div>
  );
}

export default function Insights() {
  const { user } = useAuth();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    quizApi.getMyResults()
      .then(setResult)
      .catch(() => setResult(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  const arch = user?.archetypes || [];
  const isVerified = user?.is_verified;
  const primary = user?.primary_archetypes?.[0] || arch[0];
  const secondary = user?.secondary_archetypes?.[0] || arch[1];

  if (!isVerified) {
    return (
      <div className="pa-card p-6 text-center" data-testid={IDS.insightsPage}>
        <Lock size={22} color="var(--pa-gold)" className="mx-auto mb-3" />
        <h2 className="pa-serif text-xl mb-2" style={{ color: "var(--pa-text-primary)" }}>Insights are locked</h2>
        <p className="text-sm mb-4" style={{ color: "var(--pa-text-secondary)" }}>
          Enter your access code to unlock the assessment and full insights.
        </p>
        <Link to="/unlock" className="pa-btn-gold inline-block">Enter Access Code</Link>
      </div>
    );
  }

  if (!arch.length) {
    return (
      <div className="pa-card p-6 text-center" data-testid={IDS.insightsPage}>
        <h2 className="pa-serif text-xl mb-2" style={{ color: "var(--pa-text-primary)" }}>Take the assessment</h2>
        <p className="text-sm mb-4" style={{ color: "var(--pa-text-secondary)" }}>
          Your Insights will unlock after you complete the 54-question assessment.
        </p>
        <Link to="/quiz" className="pa-btn-gold inline-block">Start Assessment</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={IDS.insightsPage}>
      <header>
        <h1 className="pa-serif text-3xl md:text-4xl" style={{ color: "var(--pa-text-primary)" }}>Your Insights</h1>
        <p className="text-sm mt-1" style={{ color: "var(--pa-text-secondary)" }}>
          Your Primary and Secondary archetype profile from the 54-question assessment.
        </p>
      </header>

      {primary && <ArchetypeInsights tag="Primary" name={primary} />}
      {secondary && <ArchetypeInsights tag="Secondary" name={secondary} />}

      <Section title="About your profile">
        <p>
          The framework identifies your Primary and Secondary archetypes only. Together, they shape how you perceive, interpret, and contribute during investigations.
        </p>
        <p style={{ color: "var(--pa-text-secondary)" }}>
          Continue in Dynamics to see how your Primary and Secondary work together, or open Field Mode for guidance during the phases of an investigation.
        </p>
      </Section>

      {result?.ranked_scores?.length > 0 && (
        <Section title="Archetype scores">
          <div className="space-y-2">
            {result.ranked_scores.map((entry) => {
              const [nm, score] = Array.isArray(entry) ? entry : [entry.name || entry.archetype, entry.score];
              return (
                <div key={nm} className="flex items-center gap-3">
                  <span className="text-sm w-40 md:w-48 flex-shrink-0" style={{ color: "var(--pa-text-primary)" }}>{nm}</span>
                  <div className="flex-1 h-2 rounded-full" style={{ background: "var(--pa-card-border)" }}>
                    <div className="h-2 rounded-full" style={{ width: `${Math.min(100, Number(score) || 0)}%`, background: "var(--pa-gold)" }} />
                  </div>
                  <span className="text-xs w-8 text-right" style={{ color: "var(--pa-text-muted)" }}>{Math.round(Number(score) || 0)}</span>
                </div>
              );
            })}
          </div>
        </Section>
      )}
    </div>
  );
}
